import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI, setActiveCompanyId, getActiveCompanyId } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ROLE_PERMISSIONS = {
  owner: {
    users: ['create', 'read', 'update', 'delete'],
    projects: ['create', 'read', 'update', 'delete'],
    categories: ['create', 'read', 'update', 'delete'],
    receipts: ['create', 'read', 'update', 'delete', 'approve'],
    time_entries: ['create', 'read', 'update', 'delete'],
    locks: ['create', 'read', 'delete'],
    exports: ['create', 'read'],
  },
  admin: {
    users: ['read'],
    projects: ['create', 'read', 'update', 'delete'],
    categories: ['create', 'read', 'update', 'delete'],
    receipts: ['create', 'read', 'update', 'delete', 'approve'],
    time_entries: ['create', 'read', 'update', 'delete'],
    locks: ['create', 'read'],
    exports: ['create', 'read'],
  },
  manager: {
    users: ['read'],
    projects: ['create', 'read', 'update'],
    categories: ['read'],
    receipts: ['create', 'read', 'update'],
    time_entries: ['create', 'read', 'update'],
    locks: ['read'],
    exports: ['read'],
  },
  worker: {
    projects: ['read'],
    categories: ['read'],
    receipts: ['create', 'read'],
    time_entries: ['create', 'read'],
    locks: ['read'],
  },
  bookkeeper: {
    users: ['read'],
    projects: ['read'],
    categories: ['read'],
    receipts: ['read'],
    time_entries: ['read'],
    locks: ['read'],
    exports: ['create', 'read'],
  },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authAPI.me();
      // F1: a tab with no company of its own adopts the one the server
      // answered with, so it stops following other tabs.
      if (!getActiveCompanyId() && response.data?.company_id) {
        setActiveCompanyId(response.data.company_id);
      }
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (err) {
      // Only clear auth on explicit 401 (invalid/expired token)
      // Keep user logged in on network errors, timeouts, 5xx, etc.
      if (err.response?.status === 401) {
        setUser(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
      // On non-401 errors, keep the cached user in place
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('auth_token');

      if (storedUser && token) {
        try {
          // Immediately restore cached user (no flash of login screen)
          const cached = JSON.parse(storedUser);
          setUser(cached);
        } catch {
          // Corrupt localStorage data
        }
        // Validate token in background — won't clear on network errors
        checkAuth();
      }
      setLoading(false);
    };

    initAuth();
  }, [checkAuth]);

  // The installed home-screen app is never reloaded — iOS just resumes it.
  // Without this, a change made in Safari (email confirmed, role changed,
  // company added) stayed invisible in the PWA and the "Staðfestu netfangið
  // þitt" banner kept showing. Refetch /auth/me whenever the app comes back
  // to the foreground, at most once every 5 seconds.
  useEffect(() => {
    let lastAt = 0;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('auth_token')) return;
      const now = Date.now();
      if (now - lastAt < 5000) return;
      lastAt = now;
      checkAuth();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData, token } = response.data;
      // F1: this tab starts in whichever company the server activated.
      setActiveCompanyId(userData?.company_id || null);
      setUser(userData);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Innskráning mistókst';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const register = useCallback(async (data) => {
    setError(null);
    try {
      const response = await authAPI.register(data);
      // Handle "added to existing user" path — server didn't issue a new
      // session (caller is presumably logged in or will log in next).
      // Return the slim payload so callers can react (refresh memberships
      // or prompt for login).
      if (response.data?.added_to_existing_user) {
        return response.data;
      }
      const { user: userData, token } = response.data;
      setUser(userData);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      const detail = err.response?.data?.detail;
      // When the server signals "email exists, prompt for current password"
      // (409 + code='email_exists_password_required'), throw a structured
      // error so the Register page can switch into the existing-user UX.
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'email_exists_password_required') {
        const e = new Error(detail.message || 'Email exists');
        e.code = detail.code;
        throw e;
      }
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Skráning mistókst');
      setError(message);
      throw new Error(message);
    }
  }, []);

  const processOAuthSession = useCallback(async (sessionId) => {
    setError(null);
    try {
      const response = await authAPI.oauthSession(sessionId);
      const { user: userData, token } = response.data;
      setUser(userData);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Google innskráning mistókst';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // Ignore logout errors
    }
    // Clear all cached API data to prevent stale data on re-login
    const { clearAll } = await import('@/utils/cache');
    clearAll();
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setActiveCompanyId(null);
  }, []);

  const switchCompany = useCallback(async (companyId) => {
    try {
      // F1: this tab moves, the others stay where they are.
      setActiveCompanyId(companyId);
      const response = await authAPI.switchCompany(companyId);
      const { user: userData, token } = response.data;
      
      // Clear all cached data to prevent cross-company data leaks
      const { clearAll } = await import('@/utils/cache');
      clearAll();
      
      // Update auth state with new company context
      setUser(userData);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return userData;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = (typeof detail === 'string' && detail)
        ? detail
        : 'Gat ekki skipt um fyrirtæki. Reyndu aftur.';
      throw new Error(message);
    }
  }, []);

  const hasPermission = useCallback((resource, action) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    const rolePermissions = ROLE_PERMISSIONS[user.role] || {};
    return rolePermissions[resource]?.includes(action) || false;
  }, [user]);

  const value = useMemo(() => {
    // ─── Beta feature flags (2026-04-29) ─────────────────────────────────
    // The /auth/me response now includes a `flags` object. We default missing
    // flags to FALSE (safest) — that way any client running an old build
    // gracefully behaves as if billing/integrations are off rather than
    // crashing or exposing a half-broken UI. When the env flag is removed
    // and the server stops sending `flags`, every user gets the safe default.
    const flags = user?.flags || {};
    const billingEnabled = !!flags.billing_enabled;
    const integrationsEnabled = !!flags.integrations_enabled;
    // is_beta is the inverse of allowlisted, sent by the server. Used by
    // the layout banner. Default true (= treat as beta) when unknown.
    const isBeta = flags.is_beta !== false;

    return {
      user,
      loading,
      error,
      login,
      register,
      logout,
      switchCompany,
      processOAuthSession,
      checkAuth,
      hasPermission,
      isAuthenticated: !!user,
      memberships: user?.memberships || [],
      // Feature flags — read these wherever you'd otherwise check
      // subscription_status to gate billing/integration UI.
      flags,
      billingEnabled,
      integrationsEnabled,
      isBeta,
    };
  }, [user, loading, error, login, register, logout, switchCompany, processOAuthSession, checkAuth, hasPermission]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
